import type {
  FloorRasterDataset,
  FloorRasterDatasetRegistration,
  FloorRasterEncodedTile,
  FloorRasterTileSpec,
} from '../utils/floorRasterTypes';
import type {
  FloorRasterWorkerRequest,
  FloorRasterWorkerResponse,
} from './floorRasterMessages';

type SuccessfulResponse = Exclude<FloorRasterWorkerResponse, { error: string }>;
type RequestPayload = FloorRasterWorkerRequest extends infer Request
  ? Request extends FloorRasterWorkerRequest
    ? Omit<Request, 'id' | 'generation'>
    : never
  : never;

type Pending = {
  generation: number;
  resolve: (response: SuccessfulResponse) => void;
  reject: (error: Error) => void;
};

export class FloorRasterWorkerClient {
  private worker: Worker | null = null;
  private failed = false;
  private nextId = 0;
  private generation = 0;
  private pending = new Map<number, Pending>();

  private ensureWorker(): Worker {
    if (this.failed || typeof Worker === 'undefined') {
      throw new Error('Floor raster worker is unavailable');
    }
    if (this.worker) return this.worker;
    try {
      this.worker = new Worker(new URL('./floorRaster.worker.ts', import.meta.url), {
        type: 'module',
      });
      this.worker.onmessage = (event: MessageEvent<FloorRasterWorkerResponse>) => {
        const response = event.data;
        const pending = this.pending.get(response.id);
        if (!pending) return;
        this.pending.delete(response.id);
        if ('error' in response) {
          pending.reject(new Error(response.error));
        } else if (
          response.generation !== pending.generation ||
          response.generation !== this.generation
        ) {
          pending.reject(new Error('Floor raster request superseded'));
        } else {
          pending.resolve(response);
        }
      };
      this.worker.onerror = () => this.fail(new Error('Floor raster worker crashed'));
      return this.worker;
    } catch (error) {
      this.failed = true;
      throw error;
    }
  }

  private fail(error: Error): void {
    this.failed = true;
    this.worker?.terminate();
    this.worker = null;
    for (const pending of this.pending.values()) pending.reject(error);
    this.pending.clear();
  }

  private request(
    message: RequestPayload
  ): Promise<SuccessfulResponse> {
    const worker = this.ensureWorker();
    const id = ++this.nextId;
    const generation = this.generation;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { generation, resolve, reject });
      worker.postMessage({ ...message, id, generation } as FloorRasterWorkerRequest);
    });
  }

  beginGeneration(): number {
    this.generation += 1;
    for (const [id, pending] of this.pending) {
      pending.reject(new Error('Floor raster request superseded'));
      this.pending.delete(id);
    }
    return this.generation;
  }

  async register(dataset: FloorRasterDataset): Promise<FloorRasterDatasetRegistration> {
    const response = await this.request({ type: 'register', dataset });
    if (response.type !== 'registered') throw new Error('Unexpected raster registration response');
    return response.registration;
  }

  async render(
    datasetId: string,
    specs: FloorRasterTileSpec[]
  ): Promise<FloorRasterEncodedTile[]> {
    if (specs.length === 0) return [];
    const response = await this.request({ type: 'render', datasetId, specs });
    if (response.type !== 'rendered') throw new Error('Unexpected raster render response');
    return response.tiles;
  }

  async release(datasetId: string): Promise<void> {
    try {
      await this.request({ type: 'release', datasetId });
    } catch {
      // A superseded/failed worker already discarded its in-memory dataset.
    }
  }

  terminate(): void {
    this.beginGeneration();
    this.worker?.terminate();
    this.worker = null;
  }

  get isAvailable(): boolean {
    return !this.failed && typeof Worker !== 'undefined';
  }
}

export const floorRasterWorkerClient = new FloorRasterWorkerClient();

